import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import { HeaderPage } from "../base/headerPage";
import { PayRequestDashboardConstant } from "../approverDashboard/index.const";
import CommonCard from "src/components/Card/Card";
import { AllDates, MonthCalendar, TimeSheetDetails, YearMonthOverview } from "src/components/MultiDateTimeCalendar";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import "./index.scss";
import { getAllPayRequestType } from "../payRequestTracker/services/getAllPayRequestType";
import { getPeriodTypes, getTimesheetListByProvider } from "./services";
import { getWorkCalendar } from "../providerPayRequestDashboard/services/getWorkCalendar";
import { getTimesheetsByProvider } from "../providerPayRequestDashboard/services/getTimesheetsByProvider";
import { getUserCredential } from "src/lib/cookies";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { getStartOfMonthInServerTz, getEndOfMonthInServerTz } from "../providerPayRequestDashboard/utils/dateUtils";
import { setSelectedDisplayMetric, clearWorkCalendarData } from "src/redux/actions/work-calendar-action";
import {
  METRIC,
  getMetricColor,
  getQuantityTotal,
  mergeQuantity,
} from "src/components/MultiDateTimeCalendar/calendarUtils";
import AddPayRequestDialog from "./components/addPayRequest/AddPayRequestDialog";
import EarningsOverviewChart from "./components/EarningsOverviewChart";
import HoursWorkedTrendChart from "./components/HoursWorkedTrendChart";
import RequestStatusOverviewChart from "./components/RequestStatusOverviewChart";
import { useCompStatementResult } from "./hooks";
import { TimesheetStatus } from "./utils/payRequestConstants";
import { calculateMonthMetrics, getMonthlyMetricBreakdown } from "./utils/calculateMonthMetrics";
import { useHistory } from "react-router-dom";
import PositionFilter from "../providerPayRequestDashboard/components/PositionFilter";
import { getProviderProfiles } from "../providerPayRequestDashboard/services/getProviderProfiles";
import {
  knownPositionKeys,
  readPositionFilter,
  scopeParams,
  showProfileControl,
  storePositionFilter,
} from "../providerPayRequestDashboard/utils/selectedProfile";

const ProviderPayRequestDashboardNew = (pageProps) => {
  const cookiesData = getUserCredential();
  const history = useHistory();
  const dispatch = useDispatch();
  const { userStatusData } = useSelector((state) => state.mobileRoleReducer?.mobileRoleData || {}, shallowEqual);
  const { workCalendarData, isLoading, selectedDisplayMetric } = useSelector(
    (state) => state.workCalendarReducer || { workCalendarData: null, isLoading: false, selectedDisplayMetric: 0 },
    shallowEqual
  );

  const [isAddPayRequestOpen, setIsAddPayRequestOpen] = useState(false);
  // Existing pay request opened in the dialog from the duplicate modal.
  const [editTask, setEditTask] = useState(null);

  // Position filter. An empty selection means All Positions. These sit above headerConfig, which reads profileRows.
  const [profileRows, setProfileRows] = useState([]);
  const [selectedPositionKeys, setSelectedPositionKeys] = useState(() => readPositionFilter());
  const scope = useMemo(() => scopeParams(profileRows, selectedPositionKeys), [profileRows, selectedPositionKeys]);

  const handlePositionChange = (keys) => {
    setSelectedPositionKeys(keys);
    storePositionFilter(keys);
  };

  const handleAddPayRequest = () => {
    setIsAddPayRequestOpen(true);
  };

  const handleViewPayRequests = () => {
    // TODO: Navigate to pay requests view
    history.push({
      pathname: "/pay_request_detail",
    });
  };

  // Pull the calendar and timesheet list back in step with what was just saved.
  const handlePayRequestSaved = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const contractTypeKey = selectedRequestType.value === "select-all" ? null : selectedRequestType.value;

    fetchWorkCalendar(year, month, contractTypeKey);
    if (providerId) {
      fetchTimesheets(providerId, year, month);
    }
  };

  const headerConfig = {
    title: PayRequestDashboardConstant.headerConfig.title,
    id: PayRequestDashboardConstant.headerConfig.id,
    add_new_fx: () => (
      <div className="button-group">
        <button type="button" onClick={handleViewPayRequests} className="button-primary">
          <i className="fas fa-eye"></i> View Pay Requests
        </button>
        <button type="button" onClick={handleAddPayRequest} className="button-primary">
          <i className="fas fa-plus"></i> Add Pay Request
        </button>
      </div>
    ),
    header_actions_fx: showProfileControl(profileRows)
      ? () => <PositionFilter rows={profileRows} selectedKeys={selectedPositionKeys} onChange={handlePositionChange} />
      : null,
  };

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [isProviderIdLoading, setIsProviderIdLoading] = useState(true);
  const [viewMode, setViewMode] = useState("month"); // "grid" or "calendar"

  const [inputValues, setInputValues] = useState(new Map());
  const [payRequestTypeOptions, setPayRequestTypeOptions] = useState([]);
  const [selectedRequestType, setSelectedRequestType] = useState({ text: "Select All", value: "select-all" });
  const [providerId, setProviderId] = useState(null);
  const [orgPositionKey, setOrgPositionKey] = useState(0);
  const [timesheetData, setTimesheetData] = useState([]);
  const [isTimesheetLoading, setIsTimesheetLoading] = useState(false);
  const [periodTypes, setPeriodTypes] = useState({
    earningOverview: {
      periodOptions: [],
      selectedPeriodType: null,
    },
    hoursWorkedTrend: {
      periodOptions: [],
      selectedPeriodType: null,
    },
    requestStatusOverview: {
      periodOptions: [],
      selectedPeriodType: null,
    },
  });

  const {
    data: compStatementData,
    isLoading: isCompStatementLoading,
    error: compStatementError,
  } = useCompStatementResult(providerId, periodTypes.earningOverview.selectedPeriodType?.id);

  const payRequestDataMap = useMemo(() => {
    const dataMap = new Map();

    if (!workCalendarData || !Array.isArray(workCalendarData)) {
      return dataMap;
    }

    workCalendarData.forEach((item) => {
      if (item.date && item.count > 0) {
        const dateKey = item.date.split("T")[0].split(" ")[0];

        dataMap.set(dateKey, {
          hours: item.total_hours ?? 0,
          minutes: item.total_minutes ?? 0,
          budget: item.total_budget ?? 0,
          quantity: item.total_quantity ?? item.total_shifts ?? 0,
          timesheetCount: item.count ?? 0,
          shifts: item.total_shifts ?? 0,
          contractTypes: Array.isArray(item.contract_type_counts)
            ? item.contract_type_counts.map((ct) => ({
                count: ct.count,
                contract_type_name: ct.contract_type_name ?? "",
                contract_type_mstr_key: ct.contract_type_mstr_key ?? 0,
                budget: ct.budget ?? 0,
                color_code: ct.color_code ?? "",
                hours: ct.hours ?? 0,
                minutes: ct.minutes ?? 0,
                shifts: ct.shifts ?? 0,
                quantity: ct.quantity ?? ct.shifts ?? 0,
              }))
            : [],
        });
      }
    });

    return dataMap;
  }, [workCalendarData]);

  // const yearMonthlyDataMap = useMemo(() => {
  //   const monthlyMap = new Map();

  //   if (!workCalendarData || !Array.isArray(workCalendarData)) {
  //     return monthlyMap;
  //   }

  //   // Group daily data by month
  //   workCalendarData.forEach((item) => {
  //     if (item.date && item.count > 0) {
  //       const dateParts = item.date.split("T")[0].split(" ")[0].split("-");
  //       const month = Number(dateParts[1]) - 1; // Convert to 0-based month index
  //       const monthKey = String(month);

  //       const existingData = monthlyMap.get(monthKey);

  //       if (existingData) {
  //         // Aggregate data for the month
  //         existingData.hours += item.total_hours ?? 0;
  //         existingData.minutes += item.total_minutes ?? 0;
  //         existingData.budget += item.total_budget ?? 0;
  //         existingData.timesheetCount += item.count ?? 0;
  //         existingData.shifts += item.total_shifts ?? 0;
  //       } else {
  //         monthlyMap.set(monthKey, {
  //           hours: item.total_hours ?? 0,
  //           minutes: item.total_minutes ?? 0,
  //           budget: item.total_budget ?? 0,
  //           timesheetCount: item.count ?? 0,
  //           shifts: item.total_shifts ?? 0,
  //           monthName: item.month_name,
  //           contractTypes: Array.isArray(item.contract_type_counts)
  //             ? item.contract_type_counts.map((ct) => ({
  //                 count: ct.count,
  //                 contract_type_name: ct.contract_type_name ?? "",
  //                 contract_type_mstr_key: ct.contract_type_mstr_key ?? 0,
  //                 budget: ct.budget ?? 0,
  //                 color_code: ct.color_code ?? "",
  //                 hours: ct.hours ?? 0,
  //                 minutes: ct.minutes ?? 0,
  //                 shifts: ct.shifts ?? 0,
  //               }))
  //             : [],
  //         });
  //       }
  //     }
  //   });

  //   return monthlyMap;
  // }, [workCalendarData]);

  const displayMetricOptions = [
    { text: "Total Hours", value: METRIC.HOURS, key: "hours" },
    { text: "Total Amount", value: METRIC.TOTAL_AMOUNT, key: "amount" },
    { text: "Total Requests", value: METRIC.COUNT, key: "count" },
    { text: "Total Quantity", value: METRIC.QUANTITY, key: "quantity" },
  ];

  const handleDateSelect = (dateKey) => {
    const newSelectedDates = new Set(selectedDates);
    if (newSelectedDates.has(dateKey)) {
      newSelectedDates.delete(dateKey);
    } else {
      newSelectedDates.add(dateKey);
    }
    setSelectedDates(newSelectedDates);
  };

  const handleMonthChange = (year, month) => {
    setCurrentDate(new Date(year, month));

    // Clear the previous month's data first so it isn't shown while the new month loads.
    dispatch(clearWorkCalendarData());
    setTimesheetData([]);

    const contractTypeKey = selectedRequestType.value === "select-all" ? null : selectedRequestType.value;
    fetchWorkCalendar(year, month, contractTypeKey);
    if (providerId) {
      fetchTimesheets(providerId, year, month);
    }
  };

  const fetchWorkCalendar = useCallback(
    async (year, month, contractTypeMstrKey = null) => {
      if (!providerId || !cookiesData.secretToken) return;

      try {
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);

        const formattedStartDate = getStartOfMonthInServerTz(monthStart);
        const formattedEndDate = getEndOfMonthInServerTz(monthEnd);

        await getWorkCalendar(
          cookiesData,
          dispatch,
          {
            providerId,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            contractTypeMstrKey,
          },
          scope
        );
      } catch (error) {
        console.error("Error fetching work calendar:", error);
      }
    },
    [providerId, cookiesData, dispatch, scope]
  );

  const fetchYearWorkCalendar = useCallback(
    async (year, contractTypeMstrKey = null) => {
      if (!providerId || !cookiesData.secretToken) return;

      try {
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);

        const formattedStartDate = getStartOfMonthInServerTz(yearStart);
        const formattedEndDate = getEndOfMonthInServerTz(yearEnd);

        await getWorkCalendar(
          cookiesData,
          dispatch,
          {
            providerId,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            groupBy: "months",
            contractTypeMstrKey,
          },
          scope
        );
      } catch (error) {
        console.error("Error fetching year work calendar:", error);
      }
    },
    [providerId, cookiesData, dispatch, scope]
  );

  const handleYearSelect = (year) => {
    const month = currentDate.getMonth();
    setCurrentDate(new Date(year, month));
    setViewMode("year");
    const contractTypeKey = selectedRequestType.value === "select-all" ? null : selectedRequestType.value;
    fetchYearWorkCalendar(year, contractTypeKey);
    if (providerId) {
      fetchYearTimesheets(providerId, year);
    }
  };

  const handleInputChange = (dateKey, value) => {
    const newInputValues = new Map(inputValues);
    newInputValues.set(dateKey, value);
    setInputValues(newInputValues);
  };

  const handleInputFocus = (dateKey) => {};

  const handleDisplayMetricChange = (metricValue) => {
    dispatch(setSelectedDisplayMetric(metricValue));
  };

  const handleRequestTypeChange = async (selectedValue) => {
    if (!selectedValue) return;

    setSelectedRequestType(selectedValue);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const contractTypeKey = selectedValue.value === "select-all" ? null : selectedValue.value;

    await fetchWorkCalendar(year, month, contractTypeKey);
  };

  const fetchProviderId = useCallback(async () => {
    try {
      setIsProviderIdLoading(true);

      const rows = await getProviderProfiles(cookiesData, dispatch);
      const primaryRow = rows[0];
      const providerIdFromAPI = primaryRow?.provider_mstr_key ?? null;

      // The profiles go in before providerId, so the first fetches already carry the scope.
      setProfileRows(rows);
      if (rows.length > 0) {
        setSelectedPositionKeys((keys) => knownPositionKeys(rows, keys));
      }
      setProviderId(providerIdFromAPI);
      setOrgPositionKey(primaryRow?.org_position_key ?? 0);
      return providerIdFromAPI;
    } catch (error) {
      console.error("Error fetching provider ID:", error);
      return null;
    } finally {
      setIsProviderIdLoading(false);
    }
  }, [cookiesData, dispatch, userStatusData]);

  const getAllCategories = useCallback(
    async (providerIdFromAPI = null) => {
      try {
        const response = await getAllPayRequestType(cookiesData, dispatch, providerId, scope);

        const typeData = response?.data?.row_result;

        if (Array.isArray(typeData)) {
          const formattedOptions = [
            { text: "Select All", value: "select-all" },
            ...typeData.map((item) => ({
              text: item.name,
              value: item.task_category_mstr_key,
            })),
          ];

          setPayRequestTypeOptions(formattedOptions);
          setSelectedRequestType({ text: "Select All", value: "select-all" });
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },
    [cookiesData, dispatch, scope]
  );

  // Only the latest Needs Action request may set the list or clear its loader;
  // an older response that arrives later is dropped.
  const timesheetRequestRef = useRef(0);

  const fetchTimesheets = useCallback(
    async (providerIdValue, year, month) => {
      if (!providerIdValue || !cookiesData.secretToken) return;

      const requestId = ++timesheetRequestRef.current;
      try {
        setIsTimesheetLoading(true);

        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);

        const formattedStartDate = getStartOfMonthInServerTz(monthStart);
        const formattedEndDate = getEndOfMonthInServerTz(monthEnd);

        const result = await getTimesheetsByProvider(
          cookiesData,
          dispatch,
          providerIdValue,
          formattedStartDate,
          formattedEndDate,
          1,
          `${TimesheetStatus.ATTESTATION_REQUIRED.mstr_key},${TimesheetStatus.REJECTED.mstr_key}`, // sortOrder
          // sortColumn, sortNumber, sortOrder, pageRowOffset and pageRows: the function's own defaults
          undefined,
          undefined,
          undefined,
          undefined,
          2000,
          scope
        );

        if (requestId === timesheetRequestRef.current) {
          setTimesheetData(result.timesheets || []);
        }
      } catch (error) {
        console.error("Error fetching timesheets by provider:", error);
      } finally {
        if (requestId === timesheetRequestRef.current) {
          setIsTimesheetLoading(false);
        }
      }
    },
    [cookiesData, dispatch, scope]
  );

  const fetchYearTimesheets = useCallback(
    async (providerIdValue, year) => {
      if (!providerIdValue || !cookiesData.secretToken) return;

      const requestId = ++timesheetRequestRef.current;
      try {
        setIsTimesheetLoading(true);

        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);

        const formattedStartDate = getStartOfMonthInServerTz(yearStart);
        const formattedEndDate = getEndOfMonthInServerTz(yearEnd);

        const result = await getTimesheetsByProvider(
          cookiesData,
          dispatch,
          providerIdValue,
          formattedStartDate,
          formattedEndDate,
          // taskCategoryMstrKey through pageRows: the function's own defaults
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          scope
        );

        if (requestId === timesheetRequestRef.current) {
          setTimesheetData(result.timesheets || []);
        }
      } catch (error) {
        console.error("Error fetching year timesheets by provider:", error);
      } finally {
        if (requestId === timesheetRequestRef.current) {
          setIsTimesheetLoading(false);
        }
      }
    },
    [cookiesData, dispatch, scope]
  );

  const yearMonthlyDataMap = useMemo(() => {
    const monthlyMap = new Map();

    if (!Array.isArray(workCalendarData)) {
      return monthlyMap;
    }

    workCalendarData.forEach((item) => {
      if (!item.date) return;

      const dateKey = item.date.split("T")[0].split(" ")[0];
      const [year, month] = dateKey.split("-").map(Number);

      // Month is zero-based: January = 0, December = 11
      const monthIndex = month - 1;
      const monthKey = String(monthIndex);

      if (!monthlyMap.has(monthKey)) {
        monthlyMap.set(monthKey, {
          hours: 0,
          minutes: 0,
          budget: 0,
          timesheetCount: 0,
          shifts: 0,
          quantity: 0,
          monthName: item.month_name,
          contractTypes: [],
        });
      }

      const monthlyData = monthlyMap.get(monthKey);

      // Aggregate daily totals
      monthlyData.hours += Number(item.total_hours) || 0;
      monthlyData.minutes += Number(item.total_minutes) || 0;
      monthlyData.budget += Number(item.total_budget) || 0;
      monthlyData.timesheetCount += Number(item.count) || 0;
      monthlyData.shifts += Number(item.total_shifts) || 0;
      monthlyData.quantity += getQuantityTotal(item.total_quantity ?? item.total_shifts);

      // Aggregate contract types
      if (Array.isArray(item.contract_type_counts)) {
        item.contract_type_counts.forEach((ct) => {
          const contractKey = ct.contract_type_mstr_key;
          let contract = monthlyData.contractTypes.find((c) => c.contract_type_mstr_key === contractKey);

          if (!contract) {
            contract = {
              count: 0,
              contract_type_name: ct.contract_type_name ?? "",
              contract_type_mstr_key: contractKey ?? 0,
              budget: 0,
              color_code: ct.color_code ?? "",
              hours: 0,
              minutes: 0,
              shifts: 0,
              quantity: 0,
            };

            monthlyData.contractTypes.push(contract);
          }

          contract.count += Number(ct.count) || 0;
          contract.budget += Number(ct.budget) || 0;
          contract.hours += Number(ct.hours) || 0;
          contract.minutes += Number(ct.minutes) || 0;
          contract.shifts += Number(ct.shifts) || 0;
          contract.quantity = mergeQuantity(contract.quantity, ct.quantity ?? ct.shifts);
        });
      }
    });

    // Convert accumulated minutes into hours where applicable
    monthlyMap.forEach((data) => {
      data.hours += Math.floor(data.minutes / 60);
      data.minutes = data.minutes % 60;

      data.contractTypes.forEach((contract) => {
        contract.hours += Math.floor(contract.minutes / 60);
        contract.minutes = contract.minutes % 60;
      });
    });

    return monthlyMap;
  }, [workCalendarData]);

  useLayoutEffect(() => {
    fetchProviderId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (providerId) {
      getAllCategories(providerId);
      // In year view the year's list comes from handleYearSelect and the effect below;
      // a month fetch here would race it.
      if (viewMode !== "year") {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        fetchTimesheets(providerId, year, month);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, currentDate, scope]);

  // The first load and every position selection: drop the old calendar, then refetch the visible month or year.
  useEffect(() => {
    if (!providerId) return;

    dispatch(clearWorkCalendarData());
    const year = currentDate.getFullYear();
    if (viewMode === "year") {
      fetchYearWorkCalendar(year);
      fetchYearTimesheets(providerId, year);
    } else {
      fetchWorkCalendar(year, currentDate.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, scope]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const rows = await getPeriodTypes(cookiesData, dispatch);
      if (cancelled) return;

      setPeriodTypes({
        earningOverview: {
          periodOptions: rows,
          selectedPeriodType: rows.length
            ? { id: rows[0].period_type_mstr_key, title: rows[0].period_type_name }
            : null,
        },
        hoursWorkedTrend: {
          periodOptions: rows,
          selectedPeriodType: rows.length
            ? { id: rows[0].period_type_mstr_key, title: rows[0].period_type_name }
            : null,
        },
        requestStatusOverview: {
          periodOptions: rows,
          selectedPeriodType: rows.length
            ? { id: rows[0].period_type_mstr_key, title: rows[0].period_type_name }
            : null,
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const currentMonthMetric = useMemo(() => {
    const monthData = yearMonthlyDataMap.get(String(currentDate.getMonth())) || {};

    switch (selectedDisplayMetric.key) {
      case "hours":
        return `${monthData.hours ?? 0}h ${monthData.minutes ?? 0}m`;

      case "amount":
        return (monthData.budget ?? 0).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        });

      case "count":
        return monthData.timesheetCount ?? 0;

      case "quantity":
        return monthData.quantity?.toFixed(2) ?? 0;

      default:
        return 0;
    }
  }, [yearMonthlyDataMap, currentDate, selectedDisplayMetric]);

  return (
    <div className="Main providerPayRequestDashboard">
      <div className="content">
        <div className="content-head">
          <HeaderPage pageProps={pageProps} headerConfig={headerConfig} />
        </div>
        <div className="dashboard-layout" style={{ "--scrollbar-color": getMetricColor(selectedDisplayMetric) }}>
          <div className="left-column">
            <EarningsOverviewChart
              providerId={providerId}
              profileRows={profileRows}
              periodTypes={periodTypes.earningOverview}
            />
            <HoursWorkedTrendChart providerId={providerId} periodTypes={periodTypes.hoursWorkedTrend} scope={scope} />
            <RequestStatusOverviewChart
              providerId={providerId}
              referenceDate={currentDate}
              periodTypes={periodTypes.requestStatusOverview}
              scope={scope}
            />
          </div>
          <div className="right-column">
            <CommonCard>
              <div className="calendar-header-row">
                <div className="header-left-section">
                  <span className="calendar-title">Work Calendar</span>
                  <div className="metric-summary" style={{ "--metric-color": getMetricColor(selectedDisplayMetric) }}>
                    <span className="metric-label">{selectedDisplayMetric.text}</span>
                    <span className="metric-value">{currentMonthMetric}</span>
                  </div>
                </div>

                <div className="header-right-section">
                  <div className="calendar-header-controls">
                    <div className="filter-controls">
                      <div className="dropdown-wrapper">
                        <label>Request Type</label>
                        <DropDownList
                          data={payRequestTypeOptions}
                          dataItemKey="value"
                          textField="text"
                          value={selectedRequestType}
                          onChange={(e) => handleRequestTypeChange(e.target.value)}
                          placeholder="Select Request Type"
                          className="filter-dropdown"
                        />
                      </div>
                    </div>
                    <div className="filter-controls">
                      <div className="dropdown-wrapper">
                        <label>Display Metrics</label>
                        <DropDownList
                          data={displayMetricOptions}
                          dataItemKey="value"
                          textField="text"
                          value={selectedDisplayMetric}
                          onChange={(e) => handleDisplayMetricChange(e.target.value)}
                          className="filter-dropdown"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {viewMode === "month" ? (
                <>
                  <MonthCalendar
                    currentDate={currentDate}
                    selectedDates={selectedDates}
                    onDateSelect={handleDateSelect}
                    onMonthChange={handleMonthChange}
                    onYearSelect={handleYearSelect}
                    payRequestData={payRequestDataMap}
                    inputMode={false}
                    inputValues={inputValues}
                    onInputChange={handleInputChange}
                    onInputFocus={handleInputFocus}
                    isLoading={isLoading}
                    metricType={selectedDisplayMetric}
                    workCalendarData={payRequestDataMap}
                  />
                  <div className="calendar-footer"></div>
                  <AllDates
                    metricType={selectedDisplayMetric}
                    workCalendarData={payRequestDataMap}
                    providerId={providerId}
                    currentDate={currentDate}
                    scope={scope}
                  />
                </>
              ) : (
                <>
                  <YearMonthOverview
                    selectedYear={currentDate.getFullYear()}
                    monthlyData={yearMonthlyDataMap}
                    metricType={selectedDisplayMetric}
                    onYearChange={handleYearSelect}
                    onMonthSelect={(month) => {
                      handleMonthChange(currentDate.getFullYear(), month);
                      setViewMode("month");
                    }}
                    backgroundColor="#E8F4F8"
                    textColor="#666"
                    isLoading={isLoading}
                  />
                  <AllDates
                    metricType={selectedDisplayMetric}
                    workCalendarData={yearMonthlyDataMap}
                    year={true}
                    providerId={providerId}
                    currentDate={currentDate}
                    scope={scope}
                  />
                </>
              )}
            </CommonCard>
            <TimeSheetDetails
              metricType={selectedDisplayMetric}
              timesheetData={timesheetData}
              providerId={providerId}
              currentDate={currentDate}
              scope={scope}
            />
          </div>
        </div>
      </div>

      {isAddPayRequestOpen && (
        <AddPayRequestDialog
          key={editTask?.timesheet_mstr_key ?? "add"}
          visible={isAddPayRequestOpen}
          task={editTask}
          onNavigateToEdit={setEditTask}
          onClose={() => {
            setIsAddPayRequestOpen(false);
            setEditTask(null);
          }}
          // An edit (a duplicate opened from create) stays on the record's own provider and position
          providerId={editTask?.provider_id ?? providerId}
          orgPositionKey={editTask?.org_position_key ?? orgPositionKey}
          defaultPositionKey={selectedPositionKeys.length === 1 ? selectedPositionKeys[0] : null}
          onSaved={handlePayRequestSaved}
        />
      )}
    </div>
  );
};

export default ProviderPayRequestDashboardNew;
